"use client";

import { useRef } from "react";
import Image from "next/image";
import { motion, useScroll, useTransform } from "framer-motion";
import { PortableText } from "@portabletext/react";

import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { GroqImage } from "@/helpers/Image";
import GradualBlur from "@/shadcn/GradualBlur";
import type { PageFabioAffiliateSchema } from "@/sanity/pages/PageFabioAffiliateTemplate";

import AnimatedSignature from "./AnimatedSignature";
import graphL from "@/public/fabio-affiliate/graph-left.svg";
import graphR from "@/public/fabio-affiliate/graph-right.svg";
import fabioTable from "@/public/fabio-affiliate/fabio-table.png";
import shadow from "@/public/fabio-affiliate/Shadow.png";
import fabio from "@/public/fabio-affiliate/fabio.png";
import squares from "@/public/fabio-affiliate/squares.svg";

import logo from "@/public/fabio-affiliate/logo.svg";

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function HeroSection({ data }: { data: PageFabioAffiliateSchema }) {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const badge1Y = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const badge2Y = useTransform(scrollYProgress, [0, 1], [0, -280]);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-clip h-[119.625rem] lg:h-[131.625rem]">
      <div className="absolute bottom-0 w-full bg-gradient-to-t to-transparent from-white/60 via-white/20 h-[20rem] z-[4]" />
      <div className="absolute bottom-0 w-full z-[5]">
        <GradualBlur
          target="parent"
          position="bottom"
          height="42.625rem"
          strength={2}
          divCount={5}
          curve="bezier"
          exponential
          opacity={1}
        />
      </div>
      <Image
        src={shadow}
        alt=""
        className="hidden object-cover absolute w-full h-full pointer-events-none lg:block z-[1]"
      />

      <motion.div
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease, delay: 0.2 }}
        className="absolute right-0 pointer-events-none z-[2] bottom-[2rem] lg:bottom-[20rem]"
        style={{ willChange: "transform" }}>
        <Image
          src={graphR}
          alt=""
          className="scale-150 pointer-events-none lg:scale-100 w-[95.25rem]"
        />
      </motion.div>

      <Image
        src={logo}
        alt=""
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none lg:translate-x-0 z-[2] top-[3.5rem] w-[15.625rem] lg:top-[6.5rem] lg:left-[24rem]"
      />

      <Image
        src={squares}
        alt=""
        className="absolute top-0 right-[38%] z-[2] lg:block hidden pointer-events-none w-[24.625rem]"
      />

      <motion.div
        initial={{ opacity: 0, y: 40, filter: "blur(16px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.9, ease, delay: 0.25 }}
        className="hidden absolute top-0 lg:block right-[11.625rem] z-[2] w-[107.75rem] h-[141.875rem]"
        style={{ willChange: "transform, filter" }}>
        <Image
          src={fabio}
          quality={100}
          alt=""
          className="object-cover w-full h-full pointer-events-none"
        />
      </motion.div>

      <div className="absolute lg:top-1/2 bottom-[21%] lg:bottom-auto lg:-translate-y-1/2 lg:right-[35%] left-[5%] z-[99] w-[19.75rem] lg:w-auto lg:left-auto">
        <motion.div style={{ y: badge1Y, willChange: "transform" }}>
          <motion.div
            initial={{ opacity: 0, filter: "blur(20px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 1, ease, delay: 0.45 }}
            style={{ willChange: "filter" }}
            className="border bg-[#F8FCFA] py-[1.25rem] px-[1.75rem] rounded-[.5rem] border-black/5 lg:px-[2.5rem]">
            <p className="m-0 font-[400] tracking-[-0.02rem] font-geist text-black/60 text-[1.75rem] lg:text-[2rem]">
              Trusted by <em className="text-black font-blacker">World Trading Champions</em>
            </p>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute lg:bottom-[30%] bottom-[12%] right-[5%] z-[99] w-[19.75rem] lg:w-auto">
        <motion.div style={{ y: badge2Y, willChange: "transform" }}>
          <motion.div
            initial={{ opacity: 0, filter: "blur(20px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 1, ease, delay: 0.6 }}
            style={{ willChange: "filter" }}
            className="border bg-[#F8FCFA] px-[1.75rem] py-[1.25rem] rounded-[.5rem] border-black/5 lg:py-[1.25rem] lg:px-[2.5rem]">
            <p className="m-0 font-[400] tracking-[-0.02rem] font-geist text-black/60 text-[1.75rem] lg:text-[2rem]">
              Order Flow & Statistical <em className="text-black font-blacker">Specialist</em>
            </p>
          </motion.div>
        </motion.div>
      </div>

      <Image
        src={fabio}
        quality={100}
        alt=""
        className="absolute bottom-0 left-1/2 w-auto -translate-x-1/2 h-[62.5rem] translate-y-[5%] lg:hidden z-[3] pointer-events-none"
      />

      <AnimatedSignature
        delay={0.25}
        drawDuration={1}
        className="hidden mx-auto lg:block lg:absolute lg:w-auto !w-[73.5rem] z-[99] lg:bottom-[23.5rem] lg:left-[23.25rem] pointer-events-none"
      />

      <div className="flex w-[min(160rem,92%)] flex-col lg:gap-[2rem] gap-[1rem] py-[8rem] lg:pl-[24.25rem] lg:pt-[20.875rem] mx-auto lg:mx-0 pt-[12rem]">
        {data?.heroChipText && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.05 }}
            className="mx-auto lg:mx-0 prose-p:italic prose-p:text-black/60 prose-p:font-blacker prose-p:font-[400] prose-p:leading-[1.5] prose-p:text-[2rem] prose-p:m-0 prose-strong:text-black prose-strong:font-[400]">
            <PortableText
              value={data.heroChipText}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {data?.heroHeading && (
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.15 }}
            className="w-[min(82.875rem,100%)] lg:text-start text-center lg:[&_img]:w-[9.5rem] [&_img]:w-[6rem] lg:[&_img]:-translate-y-[1.75rem] [&_img]:-translate-y-[.75rem] prose-h1:leading-[104%] prose-h1:via-black prose-h1:bg-gradient-to-b prose-h1:from-black prose-h1:to-[#939393] prose-h1:bg-clip-text prose-h1:text-transparent prose-h1:font-instrument-sans lg:prose-h1:text-[11rem] prose-h1:font-[500] prose-h1:text-[6rem] prose-em:font-blacker prose-em:font-[400]">
            <PortableText
              value={data.heroHeading}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {data?.heroPartner && (data.heroPartner.label || data.heroPartner.photo) && (
          <div className="flex items-center gap-[1rem]">
            {data.heroPartner.photo && (
              <GroqImage
                src={data.heroPartner.photo as any}
                alt={data.heroPartner.label ?? ""}
                width={48}
                height={48}
                className="object-cover rounded-full h-[4.8rem] w-[4.8rem]"
              />
            )}
          </div>
        )}

        {data?.heroCTA && (
          <motion.div
            initial={{ opacity: 0, y: 24, filter: "blur(12px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.8, ease, delay: 0.3 }}
            className="w-full -mt-[1rem] lg:w-fit">
            <CTAButton
              data={data.heroCTA}
              className=""
            />
          </motion.div>
        )}

        <AnimatedSignature
          delay={0.25}
          drawDuration={1}
          className="mx-auto pointer-events-none lg:hidden w-[34rem] z-[99] -mt-[2rem] -mb-[12rem]"
        />
      </div>

      <div className="flex overflow-hidden absolute bottom-0 items-center w-full z-[9] py-[3.5rem]">
        {data?.heroPills && data.heroPills.length > 0 && (
          <motion.div
            className="flex flex-shrink-0 items-center"
            animate={{ x: ["0%", "-50%"] }}
            transition={{
              duration: data.heroPills.length * 20,
              ease: "linear",
              repeat: Infinity,
            }}>
            {Array.from({ length: 16 }).flatMap((_, copyIndex) =>
              data.heroPills!.map((item, index) => (
                <div
                  key={`${copyIndex}-${index}`}
                  className="flex flex-shrink-0 items-center">
                  <p className="m-0 font-mono leading-none uppercase whitespace-nowrap text-black/60 font-[500] tracking-[0.04rem]">
                    {item.text}
                  </p>
                  <div className="flex-shrink-0 w-[.5rem] h-[.5rem] bg-[#228E71] mx-[5rem]" />
                </div>
              )),
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
